'use client';

import { Box, ClientOnly, Flex, useBreakpointValue } from '@chakra-ui/react';
import { Group, Panel, Separator } from 'react-resizable-panels';

import { useI18n } from '@/__generated__/i18n.next';
import { DialogContent, DialogRoot } from '@/components/__generated__/chakra-ui/dialog';

import { ChatPane } from './ChatPane';
import { useChatPaneSelector } from './ChatPaneContext';
import { ChatPaneOpenButton } from './ChatPaneOpenButton';
import { useChatAvailability } from './useChatAvailability';

export const WithChatPane: React.FC<{ children: React.ReactNode }> = (props) => {
  const isEmbeddedMode = useChatPaneSelector(([value]) => value.isEmbeddedMode);
  const isOpen = useChatPaneSelector(([value]) => value.isOpen);
  const { hasScope, isChatDisabled } = useChatAvailability();
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  // `isOpen` is kept across client-side navigation, so the pane also follows the availability of the current scope
  // and stays hidden until it is known. `isOpen` itself is left as is so the pane comes back on a page where chat is
  // available again.
  const shouldShowChatPane = isOpen && !isEmbeddedMode && hasScope && isChatDisabled === false;

  return (
    <>
      <Box asChild flex="1">
        <Group id="chat-pane-panels" orientation="horizontal">
          <Flex asChild direction="column">
            <Panel defaultSize={shouldShowChatPane && !isMobile ? '80%' : '100%'} id="chat-pane-main">
              {props.children}
            </Panel>
          </Flex>
          <ClientOnly fallback={<></>}>
            {/* Delay mounting DOM-dependent panels until after hydration to avoid "No group found" errors */}
            <ChatPanePanels isOpen={shouldShowChatPane && !isMobile} />
          </ClientOnly>
        </Group>
      </Box>
      {isMobile && <MobileChatPane isOpen={shouldShowChatPane} />}
      <ChatPaneOpenButton />
    </>
  );
};

const MobileChatPane: React.FC<{ isOpen: boolean }> = ({ isOpen }) => {
  const setChatState = useChatPaneSelector(([_, setValue]) => setValue);
  const t = useI18n();
  return (
    <DialogRoot
      finalFocusEl={() => document.querySelector('#chat-pane-open-button')}
      open={isOpen}
      size="full"
      onOpenChange={({ open }) => {
        setChatState((draft) => {
          draft.isOpen = open;
        });
      }}
    >
      <DialogContent aria-label={t.components.chatPane.title()} h="100dvh" minH="0" overflow="hidden">
        <ChatPane />
      </DialogContent>
    </DialogRoot>
  );
};

const ChatPanePanels: React.FC<{ isOpen: boolean }> = ({ isOpen }) => {
  if (!isOpen) return <></>;

  return (
    <>
      <Box asChild borderStartWidth={1}>
        <Separator id="chat-pane-resize-handle" />
      </Box>
      <Flex asChild direction="column">
        <Panel defaultSize="20%" id="chat-pane-sidebar" maxSize="50%" minSize="20%">
          <ChatPane />
        </Panel>
      </Flex>
    </>
  );
};
